import React, { useMemo } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Select,
  Input,
  Row,
  Col,
} from "antd";
import { EyeOutlined, GlobalOutlined } from "@ant-design/icons";
import useFormattedDate from "hooks/useFormatDate";

// badge styles
export const dnsStatusColors = {
  CONFIGURED: { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
  CONFIGURATION_FAILED: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
  PENDING: { bg: "#fff7e6", color: "#ad4e00", border: "#ffd591" },
};

export const hostingStatusColors = {
  ACTIVE: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  DNS_PENDING: { bg: "#fff7e6", color: "#ad4e00", border: "#ffd591" },
  FAILED: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

export const domainStatusColors = {
  ACTIVE: { bg: "#e6f7ff", color: "#096dd9", border: "#91d5ff" },
  INACTIVE: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
  EXPIRED: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

export const getDomainTagStyle = (type, value) => {
  const map =
    type === "dnsStatus"
      ? dnsStatusColors
      : type === "hostingStatus"
      ? hostingStatusColors
      : domainStatusColors;
  const colors = map[value];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#000",
    padding: "1px 8px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: "Inter",
  };
};

// table + filters component
export default function DomainsTable({
  domains,
  loading,
  filters,
  setFilters,
  pagination,
  onPageChange,
  onView,
}) {
  const { formatDateTime } = useFormattedDate();

  // apply filters
  const filteredDomains = useMemo(() => {
    return domains.filter((d) => {
      if (
        filters.domainName &&
        !d.domainName?.toLowerCase().includes(filters.domainName.toLowerCase())
      )
        return false;
      if (filters.dnsStatus && d.dnsStatus !== filters.dnsStatus) return false;
      if (filters.hostingStatus && d.hostingStatus !== filters.hostingStatus)
        return false;
      if (
        filters.primaryDomain !== null &&
        filters.primaryDomain !== undefined &&
        d.primaryDomain !== filters.primaryDomain
      )
        return false;
      return true;
    });
  }, [domains, filters]);

  // table columns
  const columns = [
    {
      title: "Domain",
      dataIndex: "domainName",
      key: "domainName",
      width: 240,
      ellipsis: true,
      render: (name, record) => (
        <Tooltip title={name}>
          <Space size={4} style={{ maxWidth: "100%", alignItems:"center" }}>
            <GlobalOutlined style={{ color: "#8c8c8c", flexShrink: 0 }} />
            <span
              style={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                flex: "1 1 auto",
                display: "inline-block",
              }}
            >
              {name}
            </span>
            {record.primaryDomain && (
              <Tag style={{ fontSize: "0.6rem",  display:"block" }} color="blue">
                PRIMARY
              </Tag>
            )}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Website",
      dataIndex: "websiteName",
      key: "websiteName",
      ellipsis: true,
      render: (name, record) => (
        <span style={{ fontSize: 12 }}>
          {name || record.websiteSlug || "-"}
        </span>
      ),
    },
    {
      title: "DNS Status",
      dataIndex: "dnsStatus",
      key: "dnsStatus",
      render: (status) =>
        status ? (
          <Tag style={getDomainTagStyle("dnsStatus", status)}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Hosting Status",
      dataIndex: "hostingStatus",
      key: "hostingStatus",
      render: (status) =>
        status ? (
          <Tag style={getDomainTagStyle("hostingStatus", status)}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Target IP",
      dataIndex: "targetIpAddress",
      key: "targetIpAddress",
      ellipsis: true,
      render: (val) => <span style={{ fontSize: 12 }}>{val || "-"}</span>,
    },
    {
      title: "Expires",
      dataIndex: "domainExpiresAt",
      key: "domainExpiresAt",
      render: (val) => (
        <span style={{ fontSize: 12 }}>{val ? formatDateTime(val) : "-"}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onView(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // filter bar
  const filterBar = (
    <Row gutter={[10, 5]} align="middle" style={{ marginBottom: 16 }}>
      <Col>
        <Input
          style={{ width: 220 }}
          value={filters.domainName}
          placeholder="Search domain"
          allowClear
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, domainName: e.target.value }))
          }
        />
      </Col>
      <Col>
        <Select
          style={{ width: 200 }}
          placeholder="DNS Status"
          allowClear
          value={filters.dnsStatus}
          options={[
            { value: "CONFIGURED", label: "Configured" },
            { value: "CONFIGURATION_FAILED", label: "Configuration Failed" },
            { value: "PENDING", label: "Pending" },
          ]}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, dnsStatus: val }))
          }
        />
      </Col>
      <Col>
        <Select
          style={{ width: 180 }}
          placeholder="Hosting Status"
          allowClear
          value={filters.hostingStatus}
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "DNS_PENDING", label: "DNS Pending" },
            { value: "FAILED", label: "Failed" },
          ]}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, hostingStatus: val }))
          }
        />
      </Col>
      <Col>
        <Select
          style={{ width: 140 }}
          placeholder="Primary"
          allowClear
          value={filters.primaryDomain}
          options={[
            { value: true, label: "Primary" },
            { value: false, label: "Non-Primary" },
          ]}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, primaryDomain: val }))
          }
        />
      </Col>
    </Row>
  );

  return (
    <>
      {filterBar}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredDomains}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
        }}
        onChange={(pag) => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          onPageChange({ current: pag.current, pageSize: pag.pageSize });
        }}
        onRow={(record) => ({
          onClick: () => onView(record),
          style: { cursor: "pointer" },
        })}
        scroll={{ x: 1200 }}
        size="small"
        className="custom-table"
      />
    </>
  );
}
